// C:\Users\cornn\source\repos\purehealthnexus\src\components\AiDashboard\AiMyNutritionStatus\AiUserMacros.jsx

const NODE_ENV =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.NODE_ENV) ||
  "production";

const IS_DEVELOPMENT = NODE_ENV !== "production";

function logAiUserMacros(message, detail) {
  if (!IS_DEVELOPMENT) return;
  // Logging intentionally disabled to avoid writing to console.
}

export const aiUserMacros = [
  { id: "cal", label: "Calories", value: null, target: null, pct: null },
  { id: "pro", label: "Protein", value: null, target: null, pct: null, unit: "g" },
  { id: "car", label: "Carbs", value: null, target: null, pct: null, unit: "g" },
  { id: "fat", label: "Fat", value: null, target: null, pct: null, unit: "g" },
];

class AiUserMacroBuilder {
  static createDefaultMacros() {
    const copy = aiUserMacros.map((macroEntry) => ({ ...macroEntry }));
    logAiUserMacros(
      "createDefaultMacros: returning deep copy of neutral template",
      {
        template: aiUserMacros,
        copy,
      }
    );
    return copy;
  }

  static buildMacroRow(
    macroDefinition,
    averageDailyIntake,
    comparisonVsTargets
  ) {
    if (!macroDefinition || !macroDefinition.key) {
      logAiUserMacros(
        "buildMacroRow: invalid macroDefinition, falling back to neutral row",
        {
          macroDefinition,
        }
      );

      const neutralRow = {
        id: macroDefinition?.id ?? "unknown",
        label: macroDefinition?.label ?? "Unknown",
        unit: macroDefinition?.unit,
        value: null,
        target: null,
        pct: null,
      };

      logAiUserMacros("buildMacroRow: produced neutral row", neutralRow);
      return neutralRow;
    }

    const nutrientKey = macroDefinition.key;

    const averageValue =
      typeof averageDailyIntake?.[nutrientKey] === "number"
        ? averageDailyIntake[nutrientKey]
        : null;

    const comparisonBlock = comparisonVsTargets?.[nutrientKey] || {};

    const rawTargetValue =
      typeof comparisonBlock.target === "number" &&
      Number.isFinite(comparisonBlock.target)
        ? comparisonBlock.target
        : null;

    const rawActualFromComparison =
      typeof comparisonBlock.actual === "number" &&
      Number.isFinite(comparisonBlock.actual)
        ? comparisonBlock.actual
        : null;

    const rawPercentOfTarget =
      typeof comparisonBlock.percentOfTarget === "number" &&
      Number.isFinite(comparisonBlock.percentOfTarget)
        ? comparisonBlock.percentOfTarget
        : null;

    const chosenActualValue =
      rawActualFromComparison != null ? rawActualFromComparison : averageValue;

    const roundedActual =
      chosenActualValue != null && Number.isFinite(chosenActualValue)
        ? Math.round(chosenActualValue)
        : null;

    const roundedTarget =
      rawTargetValue != null && Number.isFinite(rawTargetValue)
        ? Math.round(rawTargetValue)
        : null;

    const safePercent =
      rawPercentOfTarget != null && Number.isFinite(rawPercentOfTarget)
        ? Math.max(0, Math.min(200, Math.round(rawPercentOfTarget)))
        : null;

    const outputRow = {
      id: macroDefinition.id,
      label: macroDefinition.label,
      unit: macroDefinition.unit,
      value: roundedActual,
      target: roundedTarget,
      pct: safePercent,
    };

    logAiUserMacros("buildMacroRow: built macro row", {
      macroDefinition,
      nutrientKey,
      averageValue,
      comparisonBlock,
      rawTargetValue,
      rawActualFromComparison,
      rawPercentOfTarget,
      chosenActualValue,
      roundedActual,
      roundedTarget,
      safePercent,
      outputRow,
    });

    return outputRow;
  }

  static buildFromNutritionCalculateSnapshot(snapshot) {
    logAiUserMacros("buildFromNutritionCalculateSnapshot: invoked", {
      hasSnapshot: !!snapshot,
      hasIntakeSummary: !!snapshot?.intakeSummary,
      hasComparisonVsTargets: !!snapshot?.intakeSummary?.comparisonVsTargets,
      rangeInfo: snapshot?.rangeInfo || null,
    });

    if (
      !snapshot ||
      !snapshot.intakeSummary ||
      !snapshot.intakeSummary.comparisonVsTargets
    ) {
      logAiUserMacros(
        "buildFromNutritionCalculateSnapshot: snapshot missing or incomplete, using default macros",
        {
          snapshotPresent: !!snapshot,
        }
      );
      return AiUserMacroBuilder.createDefaultMacros();
    }

    const intakeSummary = snapshot.intakeSummary || {};
    const averageDailyIntake = intakeSummary.averageDailyIntake || {};
    const comparisonVsTargets = intakeSummary.comparisonVsTargets || {};

    logAiUserMacros(
      "buildFromNutritionCalculateSnapshot: extracted intake summary",
      {
        datesWithData: intakeSummary.datesWithData,
        totalDaysLogged: intakeSummary.totalDaysLogged,
        averageDailyIntake,
        comparisonVsTargetsKeys: Object.keys(comparisonVsTargets || {}),
      }
    );

    const macroDefinitions = [
      { id: "cal", label: "Calories", key: "energy_kcal" },
      { id: "pro", label: "Protein", key: "protein_g", unit: "g" },
      { id: "car", label: "Carbs", key: "carbohydrates_g", unit: "g" },
      { id: "fat", label: "Fat", key: "fat_g", unit: "g" },
    ];

    const builtMacros = macroDefinitions.map((macroDefinition) =>
      AiUserMacroBuilder.buildMacroRow(
        macroDefinition,
        averageDailyIntake,
        comparisonVsTargets
      )
    );

    logAiUserMacros(
      "buildFromNutritionCalculateSnapshot: finished building macros",
      {
        macroDefinitions,
        builtMacros,
      }
    );

    return builtMacros;
  }
}

export function buildAiUserMacrosFromNutritionCalculateSnapshot(
  nutritionTargetsSnapshot
) {
  logAiUserMacros(
    "buildAiUserMacrosFromNutritionCalculateSnapshot: called with snapshot",
    {
      hasSnapshot: !!nutritionTargetsSnapshot,
      hasIntakeSummary: !!nutritionTargetsSnapshot?.intakeSummary,
      hasComparisonVsTargets:
        !!nutritionTargetsSnapshot?.intakeSummary?.comparisonVsTargets,
    }
  );

  const result = AiUserMacroBuilder.buildFromNutritionCalculateSnapshot(
    nutritionTargetsSnapshot
  );

  logAiUserMacros(
    "buildAiUserMacrosFromNutritionCalculateSnapshot: returning macros",
    result
  );

  return result;
}
